/**
 * ArTable
 *
 * Public entry. Delegates to `ArTableShell`, which decides whether the
 * table's UI state is registered on `formStore` (for top-level form tables,
 * so out-of-tree consumers can read it via `formStore.getTableSlot`) or kept per-instance
 * (for entity-rendered tables and entities nested inside forms).
 */

import { FC, memo } from 'react';
import ArTableShell from './ArTableShell';
import type { ArTableId } from '#types/System.Artifacts.types';
import type { VisibilityTypeId, UIArTableColumnOverride } from '#types/System.types';

export interface ArTableProps {
    /** Table definition ID (tableTypes/{tableId}) — also used as subcollection name */
    tableId: ArTableId;
    /** Parent document path where table subcollection lives (e.g., "forms/abc123") */
    parentPath: string;
    /** Whether the table is disabled (read-only) */
    disabled?: boolean;
    /** Whether to disable the first column sticky behavior (defaults to false) */
    disableStickyFirstColumn?: boolean;
    /** Current visibility mode from FormContext */
    visibilityMode?: VisibilityTypeId;
    /** Per-column configuration from FormType layout */
    columnConfig?: UIArTableColumnOverride[];
}

const ArTable: FC<ArTableProps> = ({
    tableId,
    parentPath,
    disabled = false,
    disableStickyFirstColumn = false,
    visibilityMode,
    columnConfig,
}) => {
    if (!tableId || !parentPath) return null;

    return (
        <ArTableShell
            tableId={tableId}
            parentPath={parentPath}
            disabled={disabled}
            stickyFirstColumn={!disableStickyFirstColumn}
            visibilityMode={visibilityMode}
            columnConfig={columnConfig}
        />
    );
};

export default memo(ArTable);
